import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons/faMapMarkerAlt';
import { ROI_SCHOOL_TYPES, SCHOOL_TYPES } from '@propertypal/shared/src/constants/schoolTypes';
import { RADIUS_OPTIONS } from '@propertypal/shared/src/constants/travelDistances';
import useApi from '@propertypal/shared/src/hooks/useApi';
import useInView from '@propertypal/shared/src/hooks/useInView';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { getItem, setItem } from '@propertypal/shared/src/services/local.storage';
import { Property } from '@propertypal/shared/src/types/property';
import { SchoolsResult } from '@propertypal/shared/src/types/schools';
import { getUrl, shouldDisplayLabels } from '@propertypal/shared/src/utils/schools';
import Button from '@propertypal/web-ui/src/buttons/Button';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import SchoolsTable from '@propertypal/web-ui/src/schools/SchoolsTable';
import { MidHeading, LinkText, Text } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import router from 'next/router';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { HeaderLeft, HeaderRight, InputsRow, TabButton, TabRow } from './SchoolsData.style';

const MAX_SCHOOLS = 5;

interface Props {
  property: Property;
}

const getDefaultSchoolType = (isROI: boolean): string => {
  const schoolTypes = isROI ? ROI_SCHOOL_TYPES : SCHOOL_TYPES;
  const schoolType = getItem<string>('schoolType');

  if (schoolType && !!schoolTypes.find((s) => s.value === schoolType)) {
    return schoolType;
  }

  return schoolTypes[0].value;
};

const SchoolsData: FunctionComponent<Props> = (props) => {
  const isROI = !!props.property.countryCode && props.property.countryCode !== 'GBR';
  const appendix = isROI ? 'kms' : 'miles';
  const [schoolType, setSchoolType] = useState<string>(getDefaultSchoolType(isROI));
  const [radius, setRadius] = useState<string | null>(getItem('schoolRadius'));
  const [showAll, setShowAll] = useState<boolean>(false);
  const [schoolData, error, loading, fetchSchoolData, setSchoolData] = useApi<SchoolsResult>(
    getUrl(props.property, schoolType, radius),
    false,
  );
  const isROISchool = !!ROI_SCHOOL_TYPES.find((type) => type.value === schoolType);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { threshold: 0.5, rootMargin: '0px 0px 800px' }, fetchSchoolData);
  const [showNiLabels, showRoiLabels] = shouldDisplayLabels(schoolData, isROI);
  const showBoth = showNiLabels && showRoiLabels;

  const schools = useMemo(() => {
    if (error || loading || !schoolData) return undefined;
    const results = schoolData?.results?.[schoolType];
    if (!results) return undefined;
    if (showAll || results?.length <= MAX_SCHOOLS) return results;
    return results.slice(0, MAX_SCHOOLS);
  }, [schoolData?.results, schoolType, showAll, error, loading]);

  useEffect(() => {
    if (!error && !loading && !!schoolData?.results?.[schoolType]?.length) {
      if (schoolData?.results?.[schoolType]?.length <= MAX_SCHOOLS) setShowAll(true);
      else setShowAll(schools?.length === schoolData?.results?.[schoolType]?.length);
    } else setShowAll(false);
  }, [schools, schoolData?.results, schoolType, error, loading]);

  useEffect(() => {
    if (isInView) {
      trackGaEvent('pdp_schools_interaction', { interaction: 'School Type' });
      setSchoolData(undefined);
      fetchSchoolData();
      setItem('schoolType', schoolType);
    }
  }, [schoolType]);

  useEffect(() => {
    setItem('schoolRadius', radius);
  }, [radius]);

  useEffect(() => {
    if (schoolData?.radius && radius === null) {
      setRadius(String(schoolData.radius));
    }
  }, [schoolData]);

  return (
    <div ref={containerRef} className="pp-school-data" style={{ marginBottom: 50 }} data-testid="school-data">
      <PageWrapper
        className="pp-school-content"
        containerStyle={{ marginTop: 50, marginBottom: 30 }}
        overflow="visible"
      >
        <MidHeading mb={30}>Schools near {props.property.displayAddress}</MidHeading>

        <InputsRow>
          <HeaderRight>
            {showBoth && <h5>Northern Ireland Schools</h5>}

            {showNiLabels && (
              <TabRow>
                {SCHOOL_TYPES.map((type) => (
                  <TabButton
                    key={type.value}
                    className={schoolType === type.value ? 'pp-button pp-button-active' : 'pp-button'}
                    $selected={schoolType === type.value}
                    onClick={() => setSchoolType(type.value)}
                    type="button"
                  >
                    {type.label}
                  </TabButton>
                ))}
              </TabRow>
            )}

            {showBoth && <h5>Republic of Ireland Schools</h5>}

            {showRoiLabels && (
              <TabRow>
                {ROI_SCHOOL_TYPES.map((type) => (
                  <TabButton
                    key={type.value}
                    className={schoolType === type.value ? 'pp-button pp-button-active' : 'pp-button'}
                    $selected={schoolType === type.value}
                    onClick={() => setSchoolType(type.value)}
                    type="button"
                  >
                    {type.label}
                  </TabButton>
                ))}
              </TabRow>
            )}
          </HeaderRight>

          {radius !== null && (
            <HeaderLeft style={{ marginTop: showBoth ? 30 : 10 }}>
              <SelectInput
                id="travelDistanceSelect"
                ariaLabel="Travel Distance"
                options={RADIUS_OPTIONS(appendix)}
                onChange={(value) => {
                  trackGaEvent('pdp_schools_interaction', { interaction: 'Radius' });
                  setRadius(value);
                  setSchoolData(undefined);
                  fetchSchoolData(getUrl(props.property, schoolType, value));
                }}
                value={String(radius)}
              />

              <Button
                icon={faMapMarkerAlt}
                onClick={() =>
                  router.push(
                    `${props.property.path}/map?travelDistance=${radius}&schoolType=${schoolType}`,
                    undefined,
                    { shallow: true },
                  )
                }
              >
                Map
              </Button>
            </HeaderLeft>
          )}
        </InputsRow>
      </PageWrapper>

      <SchoolsTable
        schoolType={schoolType}
        error={error}
        loading={loading}
        schools={schools}
        isROI={isROISchool}
        // eslint-disable-next-line max-len
        errorText={`Sorry we couldn't find any schools within in a ${radius} ${appendix} travel distance of this property`}
        distanceAppendix="property"
        onViewMore={() => setShowAll(true)}
        showViewMore={!!schoolData?.results?.[schoolType]?.length && !error && !loading && !showAll}
      />

      {!isROISchool && schoolData?.results[schoolType]?.length && (
        <PageWrapper>
          <Text mt={30} mb={30} fontSize={14}>
            Figures shown are for the school year 2023/2024. Approved admissions are the maximum number of pupils a
            school can admit to its first year group. Applications include the total number of applications the school
            received. Admissions are the total number of pupils admitted by the school (including special
            circumstances). Approved enrolment is the maximum number of pupils the school can have enrolled (all year
            groups), approved by the{' '}
            <LinkText href="https://www.eani.org.uk/" target="_blank" rel="noreferrer">
              Education Authority
            </LinkText>
            . All information has been obtained via the Education Authority website and via Freedom of Information
            requests. Visit our{' '}
            <Link href="/school-finder/grammar-schools" prefetch={false}>
              <LinkText as="span">School Finder page</LinkText>
            </Link>{' '}
            for more information about transfer test grades/scores.
          </Text>
        </PageWrapper>
      )}
    </div>
  );
};

export default SchoolsData;
